/*
  Nazranaa - collection infinite scroll
  ---------------------------------------------------------------------------
  Appends the next paginated page of products into #product-grid as the shopper
  approaches the bottom of the list.

  Design rules this file follows:

  1. Progressive enhancement. The numbered pagination stays in the DOM exactly
     as before and is only hidden once this script is confirmed running. No JS,
     no IntersectionObserver, or a failed request => the normal pagination is
     shown again and nothing is lost.
  2. No new markup. Each page is fetched through Shopify's Section Rendering
     API (?section_id=), so the appended cards are byte-for-byte the same HTML
     the server already renders for page N. Filters, sorting and tag URLs are
     carried over automatically because the request reuses the current query
     string.
  3. Paced by the shopper. Every page of the collection loads automatically -
     there is no button - but exactly one request is ever in flight, and the
     next only starts once the trigger is genuinely back in view. Pages cannot
     run ahead of the scroll, so the DOM grows no faster than the shopper
     actually browses.
  4. Cheap. One IntersectionObserver for the trigger, one for the reveal
     animation, one for the URL. No scroll listeners, no polling, no library.
*/

if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();

        this.loading = false;
        this.requestedPages = new Set();

        this.onSentinelIntersect = this.onSentinelIntersect.bind(this);
        this.onRevealIntersect = this.onRevealIntersect.bind(this);
        this.onPageMarkerIntersect = this.onPageMarkerIntersect.bind(this);
      }

      connectedCallback() {
        const container = this.closest('#ProductGridContainer') || document;

        this.grid = container.querySelector('#product-grid');
        this.sentinel = this.querySelector('[data-infinite-sentinel]');
        this.spinner = this.querySelector('[data-infinite-spinner]');
        this.statusEl = this.querySelector('[data-infinite-status]');
        this.pagination = this.parentElement
          ? this.parentElement.querySelector('.pagination-wrapper')
          : null;

        this.sectionId = this.dataset.sectionId;
        this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
        this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
        this.shouldUpdateUrl = this.dataset.updateUrl === 'true';
        this.nextPage = this.currentPage + 1;

        // Bail out quietly if anything we depend on is missing. The numbered
        // pagination is still on the page, so the shopper loses nothing.
        const supported =
          'IntersectionObserver' in window &&
          typeof window.fetch === 'function' &&
          typeof window.DOMParser === 'function' &&
          typeof window.AbortController === 'function';

        if (!supported || !this.grid || !this.sentinel || !this.sectionId) return;
        if (this.nextPage > this.totalPages) return;

        this.controller = new AbortController();

        // Reveal animation for appended cards, matching the theme's own
        // scroll-trigger behaviour in assets/animations.js.
        this.revealObserver = new IntersectionObserver(this.onRevealIntersect, {
          rootMargin: '0px 0px -50px 0px',
        });

        // Keeps the address bar on the page the shopper is actually looking at,
        // so the browser Back button from a product returns to the right spot.
        if (this.shouldUpdateUrl && window.history && window.history.replaceState) {
          this.pageObserver = new IntersectionObserver(this.onPageMarkerIntersect, {
            rootMargin: '-15% 0px -70% 0px',
          });
          this.markPage(this.grid.firstElementChild, this.currentPage);
        }

        // Starts fetching well before the shopper reaches the bottom, so new
        // rows are already in place by the time they get there.
        this.sentinelObserver = new IntersectionObserver(this.onSentinelIntersect, {
          rootMargin: '800px 0px',
        });
        this.sentinelObserver.observe(this.sentinel);

        // Only now, once everything above has succeeded, do we take the
        // numbered pagination out of view.
        this.classList.add('infinite-scroll--active');
        if (this.pagination) this.pagination.classList.add('infinite-scroll-hidden');
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
        if (this.sentinelObserver) this.sentinelObserver.disconnect();
        if (this.revealObserver) this.revealObserver.disconnect();
        if (this.pageObserver) this.pageObserver.disconnect();
      }

      /* ----------------------------------------------------------------- */
      /* Observers                                                          */
      /* ----------------------------------------------------------------- */

      onSentinelIntersect(entries) {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.loadNextPage();
      }

      onRevealIntersect(entries, observer) {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('scroll-trigger--offscreen');
          observer.unobserve(entry.target);
        });
      }

      onPageMarkerIntersect(entries) {
        const visible = entries.filter((entry) => entry.isIntersecting).pop();
        if (!visible) return;
        this.updateUrlToPage(parseInt(visible.target.dataset.infiniteScrollPage, 10));
      }

      /* ----------------------------------------------------------------- */
      /* Loading                                                            */
      /* ----------------------------------------------------------------- */

      buildUrl(page) {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('page', page);
        url.searchParams.set('section_id', this.sectionId);
        return url.toString();
      }

      loadNextPage() {
        if (this.loading) return;
        if (this.nextPage > this.totalPages) return;
        if (this.requestedPages.has(this.nextPage)) return;

        const page = this.nextPage;
        this.requestedPages.add(page);
        this.loading = true;
        this.setBusy(true);

        fetch(this.buildUrl(page), { signal: this.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Status ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const appended = this.appendPage(responseText, page);
            this.loading = false;
            this.setBusy(false);

            if (!appended) {
              this.finish();
              return;
            }

            this.currentPage = page;
            this.nextPage = page + 1;

            if (this.nextPage > this.totalPages) {
              this.finish();
            } else {
              // An IntersectionObserver only fires when the intersection state
              // *changes*. Appending a page normally pushes the trigger well
              // below the fold, so nothing fires again until the shopper
              // scrolls further. But if the appended rows happened to be short
              // enough that the trigger is still in view, re-observing forces a
              // fresh callback so loading continues instead of stalling.
              this.rearmSentinel();
            }
          })
          .catch((error) => {
            this.loading = false;
            this.setBusy(false);
            if (error && error.name === 'AbortError') return;
            this.requestedPages.delete(page);
            this.fallBackToPagination();
          });
      }

      appendPage(responseText, page) {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const newGrid = doc.getElementById('product-grid');
        if (!newGrid || newGrid.children.length === 0) return false;

        const items = Array.from(newGrid.children);
        const fragment = document.createDocumentFragment();
        const toReveal = [];

        items.forEach((item, index) => {
          // Images: the section marks the first two of every page as eager.
          // From page 2 onward that would fetch off-screen images, so hand
          // them back to the browser's own lazy loading.
          item.querySelectorAll('img').forEach((img) => {
            if (img.getAttribute('loading') !== 'lazy') img.setAttribute('loading', 'lazy');
            if (img.hasAttribute('fetchpriority')) img.removeAttribute('fetchpriority');
          });

          if (item.classList.contains('scroll-trigger')) {
            item.classList.remove('scroll-trigger--cancel');
            item.classList.add('scroll-trigger--offscreen');
            if (item.hasAttribute('data-cascade')) {
              item.setAttribute('style', `--animation-order: ${index + 1};`);
            }
            toReveal.push(item);
          }

          fragment.appendChild(item);
        });

        const firstItem = items[0];
        this.grid.appendChild(fragment);

        toReveal.forEach((item) => this.revealObserver.observe(item));
        this.markPage(firstItem, page);

        this.announce(page);
        return true;
      }

      /* ----------------------------------------------------------------- */
      /* State                                                              */
      /* ----------------------------------------------------------------- */

      rearmSentinel() {
        if (!this.sentinelObserver || !this.sentinel) return;
        this.sentinelObserver.unobserve(this.sentinel);
        this.sentinelObserver.observe(this.sentinel);
      }

      finish() {
        if (this.sentinelObserver) this.sentinelObserver.disconnect();
        if (this.sentinel) this.sentinel.remove();
      }

      // Something went wrong (offline, 5xx, app timeout). Put the numbered
      // pagination back so the shopper can keep browsing by hand.
      fallBackToPagination() {
        if (this.sentinelObserver) this.sentinelObserver.disconnect();
        if (this.pagination) this.pagination.classList.remove('infinite-scroll-hidden');
        this.classList.remove('infinite-scroll--active');
      }

      setBusy(isBusy) {
        if (this.spinner) this.spinner.hidden = !isBusy;
      }

      announce(page) {
        if (!this.statusEl) return;
        const template = this.statusEl.dataset.template || 'Page [page] of [total] loaded';
        this.statusEl.textContent = template
          .replace('[page]', page)
          .replace('[total]', this.totalPages);
      }

      markPage(element, page) {
        if (!element || !this.pageObserver) return;
        element.dataset.infiniteScrollPage = page;
        this.pageObserver.observe(element);
      }

      updateUrlToPage(page) {
        if (!page || Number.isNaN(page)) return;

        const url = new URL(window.location.href);
        if (page > 1) {
          url.searchParams.set('page', page);
        } else {
          url.searchParams.delete('page');
        }
        url.searchParams.delete('section_id');

        const searchParams = url.searchParams.toString();
        if (window.location.search.replace(/^\?/, '') === searchParams) return;

        // Same state shape the theme's facet form uses, so the Back button
        // behaves consistently with filtering and sorting.
        window.history.replaceState(
          { searchParams },
          '',
          `${url.pathname}${searchParams ? `?${searchParams}` : ''}`
        );
      }
    }
  );
}
